/*
 * Texas Gift Baskets: curated-gift line grouping
 *
 * Easify adds a finished gift, magnet, and greeting card as separate cart lines.
 * This script adds the native TGB grouping properties to those lines without
 * replacing any Easify/customer properties or the cart note:
 *   _tgb_gift_id    shared, stable ID for the gift group
 *   _tgb_gift_label human-readable parent gift label
 *   _tgb_role       gift | keepsake | card
 */
(function () {
  'use strict';

  if (window.__tgbGiftGroupingLoaded) return;
  window.__tgbGiftGroupingLoaded = true;

  var nativeFetch = window.fetch.bind(window);
  var normalizePromise = null;
  var normalizeTimer = null;
  var checkoutBypassUntil = 0;

  var CARD_PRODUCT_IDS = new Set(['9816455184698']);
  var MAGNET_PRODUCT_IDS = new Set(['10180295721274', '9641239871802']);

  function route(path, fallback) {
    return (window.routes && window.routes[path]) || fallback;
  }

  function cartJsonUrl() {
    var url = route('cart_url', '/cart');
    return url.replace(/\.js(?:\?.*)?$/, '') + '.js';
  }

  function propertiesOf(item) {
    return item && item.properties && typeof item.properties === 'object'
      ? item.properties
      : {};
  }

  function propertyByKeyFragment(properties, fragments) {
    var keys = Object.keys(properties || {});
    for (var i = 0; i < keys.length; i += 1) {
      var normalizedKey = keys[i].toLowerCase();
      for (var j = 0; j < fragments.length; j += 1) {
        if (normalizedKey.indexOf(fragments[j]) !== -1) {
          return properties[keys[i]];
        }
      }
    }
    return '';
  }

  function isChosen(value) {
    var text = String(value || '').trim().toLowerCase();
    return Boolean(text) && !/^(no|none|no thanks|no thank you|skip|not now)/.test(text);
  }

  function expectedAddons(item) {
    var properties = propertiesOf(item);
    if (String(properties._tpo_add_by || '').toLowerCase() !== 'easify') {
      return null;
    }

    var magnet = propertyByKeyFragment(properties, [
      'personalized magnet',
      'custom engraved magnet option'
    ]);
    var card = propertyByKeyFragment(properties, [
      'full-color greeting card',
      'full color greeting card'
    ]);

    var expected = [];
    if (isChosen(magnet)) expected.push('keepsake');
    if (isChosen(card)) expected.push('card');
    return expected.length ? expected : null;
  }

  function addonRole(item) {
    var productId = String(item && item.product_id ? item.product_id : '');
    var title = String(
      (item && (item.product_title || item.title)) || ''
    ).toLowerCase();

    if (CARD_PRODUCT_IDS.has(productId) || title.indexOf('custom greeting card') !== -1) {
      return 'card';
    }

    if (
      MAGNET_PRODUCT_IDS.has(productId) ||
      (title.indexOf('magnet') !== -1 && title.indexOf('free with gift') !== -1)
    ) {
      return 'keepsake';
    }

    return null;
  }

  function generatedGiftId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return 'tgb-' + window.crypto.randomUUID();
    }
    return (
      'tgb-' +
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).slice(2, 12)
    );
  }

  function collectCandidate(items, anchorIndex, direction, expected) {
    var needed = new Set(expected);
    var found = [];
    var cursor = anchorIndex + direction;

    while (cursor >= 0 && cursor < items.length && found.length < expected.length) {
      var role = addonRole(items[cursor]);
      if (!role || !needed.has(role)) break;
      found.push({ index: cursor, role: role });
      needed.delete(role);
      cursor += direction;
    }

    return needed.size === 0 ? found : null;
  }

  function chooseDirection(items, anchors) {
    var forward = 0;
    var backward = 0;

    anchors.forEach(function (anchor) {
      if (collectCandidate(items, anchor.index, 1, anchor.expected)) forward += 1;
      if (collectCandidate(items, anchor.index, -1, anchor.expected)) backward += 1;
    });

    if (forward === backward) return 0;
    return forward > backward ? 1 : -1;
  }

  function buildChanges(cart) {
    var items = Array.isArray(cart && cart.items) ? cart.items : [];
    var anchors = [];

    items.forEach(function (item, index) {
      var expected = expectedAddons(item);
      if (expected) anchors.push({ index: index, expected: expected });
    });

    if (!anchors.length) return [];

    var direction = chooseDirection(items, anchors);
    if (!direction) {
      console.warn('[TGB gift grouping] Ambiguous cart order; no properties changed.');
      return [];
    }

    var claimed = new Set();
    var changes = [];
    var orderedAnchors = anchors.slice().sort(function (a, b) {
      return direction === 1 ? a.index - b.index : b.index - a.index;
    });

    orderedAnchors.forEach(function (anchor, ordinal) {
      var addOns = collectCandidate(items, anchor.index, direction, anchor.expected);
      if (!addOns || addOns.some(function (entry) { return claimed.has(entry.index); })) {
        return;
      }

      var groupEntries = [{ index: anchor.index, role: 'gift' }].concat(addOns);
      var existingIds = Array.from(
        new Set(
          groupEntries
            .map(function (entry) {
              return String(propertiesOf(items[entry.index])._tgb_gift_id || '').trim();
            })
            .filter(Boolean)
        )
      );

      // Never combine lines that already disagree about their parent gift.
      if (existingIds.length > 1) {
        console.warn('[TGB gift grouping] Conflicting existing gift IDs; group skipped.');
        return;
      }

      var giftId = existingIds[0] || generatedGiftId();
      var giftTitle = String(
        items[anchor.index].product_title || items[anchor.index].title || 'Gift'
      ).replace(/\s+/g, ' ').trim();
      var giftLabel = 'Gift ' + (ordinal + 1) + ' - ' + giftTitle;

      groupEntries.forEach(function (entry) {
        claimed.add(entry.index);
        var original = propertiesOf(items[entry.index]);
        var merged = Object.assign({}, original, {
          _tgb_gift_id: giftId,
          _tgb_gift_label: giftLabel,
          _tgb_role: entry.role
        });

        if (JSON.stringify(original) !== JSON.stringify(merged)) {
          changes.push({ line: entry.index + 1, properties: merged });
        }
      });
    });

    // Updating from the bottom prevents a changed line key from disturbing later indices.
    return changes.sort(function (a, b) { return b.line - a.line; });
  }

  async function fetchCart() {
    var response = await nativeFetch(cartJsonUrl(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to read cart (' + response.status + ').');
    return response.json();
  }

  async function applyChange(change) {
    var response = await nativeFetch(route('cart_change_url', '/cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        line: change.line,
        properties: change.properties
      })
    });

    if (!response.ok) {
      throw new Error('Unable to group cart line ' + change.line + ' (' + response.status + ').');
    }
  }

  async function normalizeGiftGroups() {
    if (normalizePromise) return normalizePromise;

    normalizePromise = (async function () {
      var cart = await fetchCart();
      var changes = buildChanges(cart);
      for (var i = 0; i < changes.length; i += 1) {
        await applyChange(changes[i]);
      }
      return changes.length;
    })();

    try {
      return await normalizePromise;
    } finally {
      normalizePromise = null;
    }
  }

  function scheduleNormalize() {
    window.clearTimeout(normalizeTimer);
    normalizeTimer = window.setTimeout(function () {
      normalizeGiftGroups().catch(function (error) {
        console.error('[TGB gift grouping]', error);
      });
    }, 350);
  }

  function requestUrl(resource) {
    if (typeof resource === 'string') return resource;
    return resource && resource.url ? resource.url : '';
  }

  function isCartAddRequest(resource) {
    return /\/cart\/add(?:\.js)?(?:\?|$)/.test(requestUrl(resource));
  }

  // Easify may use fetch to add the parent and add-ons in separate calls.
  window.fetch = async function () {
    var args = Array.prototype.slice.call(arguments);
    var response = await nativeFetch.apply(window, args);
    if (response.ok && isCartAddRequest(args[0])) scheduleNormalize();
    return response;
  };

  // Retain support if Easify uses XMLHttpRequest instead of fetch.
  var nativeXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__tgbCartAddRequest = isCartAddRequest(String(url || ''));
    if (this.__tgbCartAddRequest) {
      this.addEventListener('loadend', function () {
        if (this.status >= 200 && this.status < 300) scheduleNormalize();
      });
    }
    return nativeXhrOpen.apply(this, arguments);
  };

  function isCheckoutControl(element) {
    if (!element) return false;
    if (element.matches('button[name="checkout"], input[name="checkout"]')) return true;
    return element.matches('a[href*="/checkout"]');
  }

  async function resumeCheckout(control) {
    try {
      await normalizeGiftGroups();
      checkoutBypassUntil = Date.now() + 1500;

      if (control.tagName === 'A') {
        window.location.assign(control.href);
        return;
      }

      var form = control.form || control.closest('form');
      if (form && typeof form.requestSubmit === 'function') {
        form.requestSubmit(control);
      } else if (form) {
        form.submit();
      }
    } catch (error) {
      console.error('[TGB gift grouping]', error);
      window.alert('We could not finish organizing your gift details. Please wait a moment and try checkout again.');
    }
  }

  document.addEventListener(
    'click',
    function (event) {
      if (Date.now() < checkoutBypassUntil) return;
      var control = event.target && event.target.closest
        ? event.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]')
        : null;
      if (!isCheckoutControl(control)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      resumeCheckout(control);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleNormalize, { once: true });
  } else {
    scheduleNormalize();
  }
})();
